// CounterStatus.tsx
import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { reqFunction } from "utils/API";

import { IoIosWarning } from "react-icons/io";
import {
  MdBattery90,
  MdBattery60,
  MdBattery20,
  MdBatteryUnknown,
  MdNetworkWifi,
  MdNetworkWifi3Bar,
  MdNetworkWifi2Bar,
  MdNetworkWifi1Bar,
  MdOutlineSignalWifiBad,
  MdPower,
  MdPowerOff,
} from "react-icons/md";
import {
  FaTemperatureFull,
  FaTemperatureHalf,
  FaTemperatureQuarter,
  FaTemperatureEmpty,
} from "react-icons/fa6";
import { LuGlassWater } from "react-icons/lu";

/* =========================================================
   Color helpers (HEX fallback so swatches never look blank)
   ========================================================= */
const HEX: Record<string, string> = {
  "text-red-600": "#dc2626",
  "text-orange-500": "#f97316",
  "text-amber-500": "#f59e0b",
  "text-yellow-400": "#facc15",
  "text-lime-600": "#65a30d",
  "text-green-600": "#16a34a",
  "text-teal-600": "#0d9488",
  "text-cyan-600": "#0891b2",
  "text-blue-600": "#2563eb",
  "text-indigo-600": "#4f46e5",
  "text-purple-600": "#9333ea",
  "text-pink-600": "#db2777",
  "text-gray-500": "#6b7280",
  "text-gray-700": "#374151",
  "text-white": "#ffffff",
  "": "transparent",
};
const toBgClass = (textClass: string) =>
  textClass ? textClass.replace("text-", "bg-") : "bg-transparent";

/* =========================================================
   Types
   ========================================================= */
export type LastConsumptionInfo = {
  voltage_detail?: { Backup?: number; Battery?: number };
  SignalQuality?: number;
  temperature_detail?: { MCU?: number | null; WT?: number | null };
  Pressure?: number | null;
};

type IconKey =
  // battery + backup
  | "battery90"
  | "battery60"
  | "battery20"
  | "batteryUnknown"
  | "power"
  | "powerOff"
  // signal
  | "wifi4"
  | "wifi3"
  | "wifi2"
  | "wifi1"
  | "wifiBad"
  // temperature
  | "tempFull"
  | "tempHalf"
  | "tempQuarter"
  | "tempEmpty"
  // generic
  | "warning";

const ICONS: Record<IconKey, JSX.Element> = {
  // battery/backup
  battery90: <MdBattery90 />,
  battery60: <MdBattery60 />,
  battery20: <MdBattery20 />,
  batteryUnknown: <MdBatteryUnknown />,
  power: <MdPower />,
  powerOff: <MdPowerOff />,

  // signal
  wifi4: <MdNetworkWifi />,
  wifi3: <MdNetworkWifi3Bar />,
  wifi2: <MdNetworkWifi2Bar />,
  wifi1: <MdNetworkWifi1Bar />,
  wifiBad: <MdOutlineSignalWifiBad />,

  // temperature
  tempFull: <FaTemperatureFull />,
  tempHalf: <FaTemperatureHalf />,
  tempQuarter: <FaTemperatureQuarter />,
  tempEmpty: <FaTemperatureEmpty />,

  // generic
  warning: <IoIosWarning />,
};

type RangeRule = {
  gt: number | null;
  lte?: number | null;
  className: string;
  icon: IconKey;
  rotate?: boolean;
};

type MetricConfig = {
  label: string;
  tooltipUnit?: string;
  decimals?: number;
  ranges: RangeRule[];
};

const METRIC_ORDER: (keyof CounterStatusConfig)[] = [
  "battery",
  "signal",
  "backup",
  "temperature",
  "waterTemperature",
  "pressure",
];

const METRIC_LABELS: Record<keyof CounterStatusConfig, string> = {
  battery: "باتری بکاپ",
  signal: "سیگنال",
  backup: "برق و باتری",
  temperature: "دمای دستگاه",
  waterTemperature: "دمای آب",
  pressure: "فشار",
};

export type CounterStatusConfig = {
  battery: MetricConfig;
  signal: MetricConfig;
  backup: MetricConfig;
  temperature: MetricConfig;
  waterTemperature: MetricConfig;
  pressure: MetricConfig;
};

/* =========================================================
   Allowed icons per metric (whitelists)
   ========================================================= */
// Battery + Backup: ALL requested battery + power icons, plus warning
const ICONS_BATT_AND_BACKUP: IconKey[] = [
  "battery90",
  "battery60",
  "battery20",
  "batteryUnknown",
  "power",
  "powerOff",
  "warning",
];
const ICONS_SIGNAL: IconKey[] = [
  "wifi4",
  "wifi3",
  "wifi2",
  "wifi1",
  "wifiBad",
  "warning",
];
const ICONS_TEMP: IconKey[] = [
  "tempFull",
  "tempHalf",
  "tempQuarter",
  "tempEmpty",
  "warning",
];

/* =========================================================
   Defaults (fallback only)
   ========================================================= */
const DEFAULT_CONFIG: CounterStatusConfig = {
  battery: {
    label: "باتری بکاپ",
    tooltipUnit: "ولت",
    decimals: 2,
    ranges: [
      { gt: 3.7, className: "text-yellow-400", icon: "warning", rotate: true },
      { gt: 3.5, className: "text-green-600", icon: "battery90", rotate: true },
      {
        gt: 3.3,
        className: "text-yellow-600",
        icon: "battery60",
        rotate: true,
      },
      { gt: 0, className: "text-red-600", icon: "battery20", rotate: true },
      { gt: null, className: "", icon: "batteryUnknown" },
    ],
  },
  signal: {
    label: "سیگنال",
    decimals: 0,
    ranges: [
      { gt: 31, className: "text-yellow-400", icon: "warning" },
      { gt: 25, className: "text-green-600", icon: "wifi4" },
      { gt: 20, className: "text-green-600", icon: "wifi3" },
      { gt: 13, className: "text-yellow-600", icon: "wifi2" },
      { gt: 0, className: "text-red-600", icon: "wifi1" },
      { gt: null, className: "", icon: "wifiBad" },
    ],
  },
  backup: {
    label: "باتری و برق",
    tooltipUnit: "ولت",
    decimals: 2,
    ranges: [
      { gt: 5.5, className: "text-yellow-400", icon: "warning" },
      { gt: 4, className: "text-blue-600", icon: "power" },
      { gt: 3.5, className: "text-green-600", icon: "battery90", rotate: true },
      {
        gt: 3.3,
        className: "text-yellow-600",
        icon: "battery60",
        rotate: true,
      },
      { gt: 1.5, className: "text-red-600", icon: "battery20", rotate: true },
      { gt: 0, className: "text-red-600", icon: "powerOff" },
      { gt: null, className: "", icon: "batteryUnknown" },
    ],
  },
  temperature: {
    label: "دما",
    tooltipUnit: "°C",
    decimals: 0,
    ranges: [
      { gt: 65, className: "text-yellow-400", icon: "warning" },
      { gt: 35, className: "text-red-600", icon: "tempFull" },
      { gt: 20, className: "text-orange-500", icon: "tempHalf" },
      { gt: 0, className: "text-green-600", icon: "tempQuarter" },
      { gt: null, className: "text-blue-600", icon: "tempEmpty" },
    ],
  },
  waterTemperature: {
    label: "دمای آب",
    tooltipUnit: "°C",
    decimals: 0,
    ranges: [
      { gt: 65, className: "text-yellow-400", icon: "warning" },
      { gt: 35, className: "text-red-600", icon: "tempFull" },
      { gt: 20, className: "text-orange-500", icon: "tempHalf" },
      { gt: 0, className: "text-green-600", icon: "tempQuarter" },
      { gt: null, className: "text-blue-600", icon: "tempEmpty" },
    ],
  },
  pressure: {
    label: "فشار",
    tooltipUnit: "bar",
    decimals: 2,
    ranges: [
      { gt: 0.15, className: "text-red-600", icon: "warning" },
      { gt: 0.05, className: "text-yellow-400", icon: "warning" },
      { gt: null, className: "text-green-600", icon: "warning" },
    ],
  },
};

/* =========================================================
   Data layer (React Query)
   ========================================================= */
const CONFIG_QUERY_KEY = ["counterStatusConfig"];

export function useCounterStatusConfig() {
  return useQuery({
    queryKey: CONFIG_QUERY_KEY,
    queryFn: async () => {
      const res = await reqFunction("Config/admin/getCounterStatus", {});
      const serverCfg = (res?.data?.config ||
        {}) as Partial<CounterStatusConfig>;
      // merge so missing keys fall back to defaults
      return { ...DEFAULT_CONFIG, ...serverCfg };
    },
    placeholderData: DEFAULT_CONFIG,
  });
}

function useSaveCounterStatusConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (config: CounterStatusConfig) =>
      reqFunction("Config/admin/editCounterStatus", { config }),
    onSuccess: () => qc.invalidateQueries({ queryKey: CONFIG_QUERY_KEY }),
  });
}

/* =========================================================
   Rule engine (shared)
   ========================================================= */
function pickRule(
  valueRaw: number | null | undefined,
  metric: MetricConfig
): RangeRule {
  const value = valueRaw ?? null;
  const ordered = [...metric.ranges].sort(
    (a, b) => (b.gt ?? -Infinity) - (a.gt ?? -Infinity)
  );
  if (value === null)
    return ordered.find((r) => r.gt === null) ?? ordered[ordered.length - 1];
  for (const r of ordered) {
    const lowerOk = r.gt === null ? true : value > r.gt;
    const upperOk = r.lte == null ? true : value <= r.lte;
    if (lowerOk && upperOk) return r;
  }
  return ordered[ordered.length - 1];
}

function fmt(v: number, decimals = 0) {
  return Number.isFinite(v) ? v.toFixed(decimals) : "__";
}

/* =========================================================
   Color picker (visual)
   ========================================================= */
const COLOR_PALETTE: string[] = [
  "text-red-600",
  "text-orange-500",
  "text-amber-500",
  "text-yellow-400",
  "text-lime-600",
  "text-green-600",
  "text-teal-600",
  "text-cyan-600",
  "text-blue-600",
  "text-indigo-600",
  "text-purple-600",
  "text-pink-600",
  "text-gray-500",
  "text-gray-700",
  "text-white",
  "",
];

const ColorSwatch: React.FC<{
  value: string; // e.g. "text-red-600" or ""
  selected: boolean;
  onClick: () => void;
  title?: string;
}> = ({ value, selected, onClick, title }) => {
  const bgClass = toBgClass(value);
  const hex = HEX[value] ?? "transparent";
  return (
    <button
      type="button"
      title={title || value || "بدون رنگ"}
      onClick={onClick}
      className={`relative h-7 w-7 overflow-hidden rounded-full border ${
        selected ? "ring-2 ring-blue-500 ring-offset-2" : ""
      }`}
      style={{
        backgroundImage:
          "linear-gradient(45deg,#eee 25%,transparent 25%),linear-gradient(-45deg,#eee 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#eee 75%),linear-gradient(-45deg,transparent 75%,#eee 75%)",
        backgroundSize: "8px 8px",
        backgroundPosition: "0 0,0 4px,4px -4px,-4px 0px",
      }}
    >
      <span
        className={`absolute inset-0 m-auto h-4 w-4 rounded-full border ${bgClass}`}
        style={{ backgroundColor: hex, borderColor: "#e5e7eb" }}
      />
    </button>
  );
};

const ColorPicker: React.FC<{
  value: string;
  onChange: (next: string) => void;
}> = ({ value, onChange }) => (
  <div className="flex flex-wrap items-center gap-2">
    {/* swatches first */}
    <div className="flex flex-wrap gap-2">
      {COLOR_PALETTE.map((c) => (
        <ColorSwatch
          key={c || "none"}
          value={c}
          selected={value === c}
          onClick={() => onChange(c)}
          title={c || "بدون رنگ"}
        />
      ))}
    </div>

    {/* manual input after */}
    <input
      type="text"
      className="w-44 rounded border px-2 py-1 font-mono"
      placeholder="مثال: text-green-600"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  </div>
);

/* =========================================================
   Icon picker (shows only whitelisted icons for the metric)
   ========================================================= */
const IconPicker: React.FC<{
  value: IconKey;
  onChange: (next: IconKey) => void;
  colorClass?: string;
  allow: IconKey[];
}> = ({ value, onChange, colorClass = "", allow }) => {
  const entries = allow.map(
    (key) => [key, ICONS[key]] as [IconKey, JSX.Element]
  );
  return (
    <div className="grid grid-cols-8 gap-2">
      {entries.map(([key, El]) => {
        const isSelected = value === key;
        return (
          <button
            key={key}
            type="button"
            onClick={() => onChange(key)}
            className={`flex items-center justify-center rounded-lg border p-2 hover:bg-gray-50 ${
              isSelected ? "ring-2 ring-blue-500 ring-offset-1" : ""
            }`}
            title={key}
            aria-label={key}
          >
            <span
              className={`text-2xl ${
                isSelected ? colorClass || "text-gray-700" : "text-gray-400"
              }`}
            >
              {El}
            </span>
          </button>
        );
      })}
    </div>
  );
};

/* =========================================================
   Range row editor
   ========================================================= */
const RangeRowEditor: React.FC<{
  value: RangeRule;
  onChange: (next: RangeRule) => void;
  canDelete?: boolean;
  onDelete?: () => void;
  allowIcons: IconKey[];
  showRotate?: boolean;
}> = ({
  value,
  onChange,
  canDelete,
  onDelete,
  allowIcons,
  showRotate = false,
}) => {
  return (
    <div className="grid grid-cols-12 items-start gap-3">
      <div className="col-span-12 md:col-span-3">
        <label className="text-xs text-gray-500">بزرگ‌تر از</label>
        <input
          type="number"
          className="w-full rounded border px-2 py-1"
          value={value.gt === null ? "" : value.gt}
          placeholder="_"
          onChange={(e) =>
            onChange({
              ...value,
              gt: e.target.value === "" ? null : Number(e.target.value),
            })
          }
        />
      </div>

      <div className="col-span-12 md:col-span-3">
        <label className="text-xs text-gray-500">کوچک‌تر یا مساوی</label>
        <input
          type="number"
          className="w-full rounded border px-2 py-1"
          value={value.lte == null ? "" : value.lte}
          placeholder="_"
          onChange={(e) =>
            onChange({
              ...value,
              lte: e.target.value === "" ? undefined : Number(e.target.value),
            })
          }
        />
      </div>

      <div className="col-span-12">
        <label className="text-xs text-gray-500">رنگ</label>
        <ColorPicker
          value={value.className}
          onChange={(next) => onChange({ ...value, className: next })}
        />
      </div>

      {allowIcons.length > 0 && (
        <div className="col-span-12">
          <label className="text-xs text-gray-500">آیکن</label>
          <IconPicker
            value={value.icon}
            onChange={(next) => onChange({ ...value, icon: next })}
            colorClass={value.className}
            allow={allowIcons}
          />
        </div>
      )}

      {showRotate && (
        <div className="col-span-12 flex items-center gap-2 md:col-span-3">
          <label className="whitespace-nowrap text-xs text-gray-500">
            چرخش 90°
          </label>
          <input
            type="checkbox"
            checked={!!value.rotate}
            onChange={(e) =>
              onChange({ ...value, rotate: e.target.checked || undefined })
            }
          />
        </div>
      )}

      {canDelete && (
        <div className="col-span-12">
          <button
            type="button"
            className="text-xs text-red-600 hover:underline"
            onClick={onDelete}
          >
            حذف این ردیف
          </button>
        </div>
      )}
    </div>
  );
};

/* =========================================================
   Metric editor (per-metric icon whitelist)
   ========================================================= */
const MetricEditor: React.FC<{
  name: keyof CounterStatusConfig;
  metric: MetricConfig;
  onChange: (next: MetricConfig) => void;
}> = ({ name, metric, onChange }) => {
  const allow =
    name === "battery"
      ? ICONS_BATT_AND_BACKUP
      : name === "backup"
      ? ICONS_BATT_AND_BACKUP
      : name === "signal"
      ? ICONS_SIGNAL
      : name === "pressure"
      ? []
      : ICONS_TEMP;

  const showRotate = name === "battery" || name === "backup";

  return (
    <div
      id={`sec-${name}`}
      className="rounded-2xl border bg-white p-4 shadow-sm"
    >
      <div className="mb-3 flex items-center justify-between">
        <div className="text-sm font-semibold">{metric.label}</div>
      </div>

      <div className="mb-4 grid gap-3 md:grid-cols-4">
        <div>
          <label className="text-xs text-gray-500">برچسب</label>
          <input
            className="w-full rounded border px-2 py-1"
            value={metric.label}
            onChange={(e) => onChange({ ...metric, label: e.target.value })}
          />
        </div>
        <div>
          <label className="text-xs text-gray-500">واحد</label>
          <input
            className="w-full rounded border px-2 py-1"
            value={metric.tooltipUnit ?? ""}
            onChange={(e) =>
              onChange({ ...metric, tooltipUnit: e.target.value || undefined })
            }
          />
        </div>
        <div>
          <label className="text-xs text-gray-500">رقم اعشار</label>
          <input
            type="number"
            className="w-full rounded border px-2 py-1"
            value={metric.decimals ?? 0}
            onChange={(e) =>
              onChange({ ...metric, decimals: Number(e.target.value) })
            }
          />
        </div>
      </div>

      <div className="space-y-3">
        {metric.ranges.map((r, idx) => (
          <div key={idx} className="rounded-xl border bg-gray-50 p-3">
            <RangeRowEditor
              value={r}
              onChange={(next) => {
                const ranges = metric.ranges.slice();
                ranges[idx] = next;
                onChange({ ...metric, ranges });
              }}
              canDelete={metric.ranges.length > 1}
              onDelete={() => {
                const ranges = metric.ranges.slice();
                ranges.splice(idx, 1);
                onChange({ ...metric, ranges });
              }}
              allowIcons={allow}
              showRotate={showRotate}
            />
          </div>
        ))}

        <button
          type="button"
          className="text-xs text-blue-700 hover:underline"
          onClick={() =>
            onChange({
              ...metric,
              ranges: [
                ...metric.ranges,
                { gt: null, className: "", icon: allow[0] },
              ],
            })
          }
        >
          + افزودن قانون
        </button>
      </div>
    </div>
  );
};

/* =========================================================
   Settings view (no sidebar; just scroll)
   ========================================================= */
export const CounterStatusSettings: React.FC = () => {
  const { data: cfg } = useCounterStatusConfig();
  const [draft, setDraft] = useState<CounterStatusConfig>(cfg);
  const save = useSaveCounterStatusConfig();

  // کدام آیتم انتخاب شده است (تب فعال)
  const [selectedMetric, setSelectedMetric] =
    useState<keyof CounterStatusConfig>("battery");

  React.useEffect(() => setDraft(cfg), [cfg]);

  const handleMetricChange = (next: MetricConfig) => {
    setDraft((prev) => ({
      ...prev,
      [selectedMetric]: next,
    }));
  };

  const currentMetric = draft[selectedMetric];

  return (
    <div className="mt-3 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold">تنظیمات وضعیت کنتور</h3>
        <div className="flex gap-2">
          <button
            className="rounded-xl border px-3 py-1.5 shadow-sm hover:bg-gray-50"
            onClick={() => setDraft(cfg)}
            disabled={save.isPending}
          >
            بازنشانی
          </button>
          <button
            className="rounded-xl border bg-blue-600 px-3 py-1.5 text-white shadow-sm hover:bg-blue-700 disabled:opacity-50"
            onClick={() => save.mutate(draft)}
            disabled={save.isPending}
          >
            {save.isPending ? "در حال ذخیره..." : "ذخیره تغییرات"}
          </button>
        </div>
      </div>

      {/* تب‌ها برای انتخاب آیتم */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {METRIC_ORDER.map((key) => {
          const isActive = key === selectedMetric;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setSelectedMetric(key)}
              className={`whitespace-nowrap rounded-full px-3 py-1 text-sm transition ${
                isActive
                  ? "bg-blue-600 text-white shadow-sm"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              {METRIC_LABELS[key]}
            </button>
          );
        })}
      </div>

      {/* فقط تنظیمات آیتم انتخاب‌شده */}
      <div className="max-h-[70vh] overflow-y-auto pr-1">
        {currentMetric && (
          <MetricEditor
            name={selectedMetric}
            metric={currentMetric}
            onChange={handleMetricChange}
          />
        )}
      </div>
    </div>
  );
};

/* =========================================================
   Pressure gauge config
   ========================================================= */
const PressureGauge: React.FC<{
  value: number | null | undefined;
  config: MetricConfig;
  /** numeric range of the gauge (e.g. 0–1, 0–5, 0–10, ...) */
  min?: number;
  max?: number;
}> = ({ value, config, min = 0, max = 1 }) => {
  if (value === null || value === undefined) return null;

  // ---- sort ranges (high → low) just for picking colors
  const ranges = [...config.ranges].sort(
    (a, b) => (b.gt ?? -Infinity) - (a.gt ?? -Infinity)
  );

  // need at least 3 ranges to look nice
  if (ranges.length < 3) return null;

  // high / mid / low colors from config (purely visual)
  const colorRight = HEX[ranges[0].className] || "#ef4444"; // high
  const colorMid = HEX[ranges[1].className] || "#facc15"; // mid
  const colorLeft = HEX[ranges[ranges.length - 1].className] || "#22c55e"; // low

  // ---- REAL numeric domain comes from props (min / max), not from ranges
  const domainMin = Math.min(min, max);
  const domainMax = Math.max(min, max);

  // clamp value to [domainMin, domainMax]
  const v = Math.min(domainMax, Math.max(domainMin, value));

  // normalized position of the pointer in [0,1]
  const t =
    domainMax - domainMin > 0 ? (v - domainMin) / (domainMax - domainMin) : 0.5;

  const startAngle = -60; // left
  const endAngle = 60; // right
  const angle = startAngle + t * (endAngle - startAngle);

  const ARC_THICKNESS = 10;
  const INNER_BORDER = 0;
  const pointerColor = "#2e252e";

  // where the middle color sits in the gradient – we base it on mid of domain
  const midVal = (domainMin + domainMax) / 2;
  const midStop =
    domainMax - domainMin > 0
      ? (midVal - domainMin) / (domainMax - domainMin)
      : 0.5;

  const title = `${config.label}: ${v.toFixed(config.decimals ?? 2)} ${
    config.tooltipUnit || ""
  }`;

  return (
    <div className="h-10 w-12" title={title}>
      <svg viewBox="0 0 100 60" className="h-full w-full">
        <defs>
          {/* left → mid → right gradient across the arc */}
          <linearGradient
            id="pressureGradient"
            x1="0%"
            y1="100%"
            x2="100%"
            y2="100%"
          >
            <stop offset="0%" stopColor={colorLeft} />
            <stop offset={`${midStop * 100}%`} stopColor={colorMid} />
            <stop offset="100%" stopColor={colorRight} />
          </linearGradient>
        </defs>

        {/* thick gradient outer arc */}
        <path
          d="M10 50 A40 40 0 0 1 90 50"
          fill="none"
          stroke="url(#pressureGradient)"
          strokeWidth={ARC_THICKNESS}
          strokeLinecap="round"
        />

        {/* inner white border to keep inside clean */}
        <path
          d="M10 50 A40 40 0 0 1 90 50"
          fill="none"
          stroke="#ffffff"
          strokeWidth={INNER_BORDER}
        />

        {/* needle – strictly based on numeric pressure */}
        <g transform={`rotate(${angle} 50 50)`}>
          <line
            x1="50"
            y1="50"
            x2="50"
            y2="18"
            stroke={pointerColor}
            strokeWidth="6"
            strokeLinecap="round"
          />
        </g>

        {/* center knob */}
        <circle cx="50" cy="50" r="6" fill={pointerColor} />
      </svg>
    </div>
  );
};

const WaterTankIcon: React.FC<{
  rule: RangeRule;
  title: string;
  compact?: boolean; // برای موبایل
}> = ({ rule, title, compact = false }) => {
  const tankSize = compact ? "h-6 w-7" : "h-7 w-9";

  return (
    <div className="flex items-center" title={title}>
      {/* مخزن آب آبی */}
      {/* <div className={`relative flex ${tankSize} items-center justify-center`}> */}
      {/* بدنه مخزن */}
      {/* <div className="h-full w-full rounded-md border border-blue-400 bg-blue-100/70 dark:bg-blue-900/40" /> */}
      {/* سطح آب (نصف پایین) */}
      {/* <div className="absolute bottom-0 h-0 w-full rounded-b-md bg-blue-300/80" /> */}

      <span
        className={`absolute text-xl ${rule.className} ${
          rule.rotate ? "rotate-90" : ""
        }`}
      >
        {ICONS[rule.icon]}
      </span>
      {/* </div> */}
    </div>
  );
};

/* =========================================================
   Renderer (use anywhere in your UI)
   ========================================================= */
export const CounterStatusRenderer: React.FC<{
  status: LastConsumptionInfo;
  showTemper?: boolean;
  isMobile?: boolean;
  hasPressure?: boolean;
  mode?:
    | "row"
    | "battery"
    | "signal"
    | "backup"
    | "pressure"
    | "tempDevice"
    | "tempWater";
}> = ({
  status,
  showTemper = true, // instead of removing for now, I set the default to true
  isMobile = false,
  hasPressure = false,
  mode = "row",
}) => {
  const { data: cfg } = useCounterStatusConfig();

  const battery = status?.voltage_detail?.Backup ?? 0;
  const signal = status?.SignalQuality ?? 0;
  const backupBattery = status?.voltage_detail?.Battery ?? 0;

  const temperValue = status?.temperature_detail?.MCU ?? null;
  const waterTemp = status?.temperature_detail?.WT ?? null;

  const pressure =
    status?.Pressure !== null && status?.Pressure !== undefined
      ? status.Pressure
      : null;

  const rBattery = pickRule(battery, cfg.battery);
  const rSignal = pickRule(signal, cfg.signal);
  const rBackup = pickRule(backupBattery, cfg.backup);
  const rTemp = pickRule(temperValue, cfg.temperature);
  const rWaterTemp = pickRule(waterTemp, cfg.waterTemperature);

  const tBattery = `${battery === 0 ? "__" : battery} ${
    cfg.battery.tooltipUnit ?? ""
  }`.trim();
  const tSignal = `${signal === 0 ? "__" : signal}`;
  const tBackup = `${backupBattery === 0 ? "__" : backupBattery} ${
    cfg.backup.tooltipUnit ?? ""
  }`.trim();
  const tTempDevice =
    temperValue == null
      ? "__"
      : `${temperValue} ${cfg.temperature.tooltipUnit ?? ""}`;
  const tTempWater =
    waterTemp == null
      ? ""
      : `${waterTemp} ${cfg.temperature.tooltipUnit ?? ""}`;

  const IconSpan: React.FC<{ rule: RangeRule; title: string }> = ({
    rule,
    title,
  }) => (
    <span
      className={`mx-1 text-xl ${rule.className} ${
        rule.rotate ? "rotate-90" : ""
      }`}
      title={title}
    >
      {ICONS[rule.icon]}
    </span>
  );

  const batteryCellIcon = <IconSpan rule={rBattery} title={tBattery} />;
  const signalCellIcon = <IconSpan rule={rSignal} title={tSignal} />;
  const backupCellIcon = <IconSpan rule={rBackup} title={tBackup} />;

  const pressureCellIcon =
    hasPressure && cfg.pressure ? (
      pressure != null ? (
        <PressureGauge
          value={pressure}
          config={cfg.pressure}
          min={0}
          max={1} // or whatever range you want (e.g. 0–5 bar)
        />
      ) : (
        <span className="text-xs text-gray-400">__</span>
      )
    ) : null;

  const tempDeviceCellIcon = showTemper ? (
    <IconSpan rule={rTemp} title={`دمای دستگاه: ${tTempDevice}`} />
  ) : null;

  const tempWaterCellIcon =
    showTemper && waterTemp != null ? (
      <WaterTankIcon rule={rWaterTemp} title={`دمای آب: ${tTempWater}`} />
    ) : showTemper ? (
      <span className="text-xs text-gray-400">__</span>
    ) : null;

  /* ============================
     MOBILE: numbers only – no shapes
     ============================ */
  if (isMobile) {
    // for single-column usage in the table
    if (mode !== "row") {
      let valueText = "__";
      let unit = "";
      let className = "";

      switch (mode) {
        case "battery":
          valueText =
            battery === 0 ? "__" : fmt(battery, cfg.battery.decimals ?? 2);
          unit = cfg.battery.tooltipUnit ?? "";
          className = rBattery.className;
          break;
        case "signal":
          valueText =
            signal === 0 ? "__" : fmt(signal, cfg.signal.decimals ?? 0);
          className = rSignal.className;
          break;
        case "backup":
          valueText =
            backupBattery === 0
              ? "__"
              : fmt(backupBattery, cfg.backup.decimals ?? 2);
          unit = cfg.backup.tooltipUnit ?? "";
          className = rBackup.className;
          break;
        case "pressure":
          if (!hasPressure) {
            valueText = "__";
          } else {
            valueText =
              pressure == null
                ? "__"
                : fmt(pressure, cfg.pressure.decimals ?? 2);
            unit = cfg.pressure.tooltipUnit ?? "";
          }
          className = ""; // you can keep empty or derive from ranges if you want
          break;
        case "tempDevice":
          valueText =
            temperValue == null
              ? "__"
              : fmt(temperValue, cfg.temperature.decimals ?? 0);
          unit = cfg.temperature.tooltipUnit ?? "";
          className = rTemp.className;
          break;
        case "tempWater":
          valueText =
            waterTemp == null
              ? "__"
              : fmt(waterTemp, cfg.waterTemperature.decimals ?? 0);
          unit = cfg.waterTemperature.tooltipUnit ?? "";
          className = rWaterTemp.className;
          break;
      }

      return (
        <div className="flex items-center justify-center text-[11px] font-bold">
          <span className={className}>
            {valueText}
            {unit ? ` ${unit}` : ""}
          </span>
        </div>
      );
    }

    // for any other mobile usage with mode="row" – still numbers only
    return (
      <div className="flex items-center justify-around text-[11px] font-bold">
        <span className={rBattery.className}>
          {battery === 0 ? "__" : fmt(battery, cfg.battery.decimals ?? 2)}
        </span>
        <span className={rSignal.className}>
          {signal === 0 ? "__" : fmt(signal, cfg.signal.decimals ?? 0)}
        </span>
        <span className={rBackup.className}>
          {backupBattery === 0
            ? "__"
            : fmt(backupBattery, cfg.backup.decimals ?? 2)}
        </span>

        {hasPressure && cfg.pressure && (
          <span className="text-gray-700">
            {pressure == null
              ? "__"
              : fmt(pressure, cfg.pressure.decimals ?? 2)}
          </span>
        )}

        {showTemper && (
          <>
            <span className={rTemp.className}>
              {temperValue == null
                ? "__"
                : fmt(temperValue, cfg.temperature.decimals ?? 0)}
            </span>
            <span className={rWaterTemp.className}>
              {waterTemp == null
                ? "__"
                : fmt(waterTemp, cfg.waterTemperature.decimals ?? 0)}
            </span>
          </>
        )}
      </div>
    );
  }

  /* ============================
     DESKTOP: icons / gauge (unchanged)
     ============================ */

  const pickSingleCellDesktop = () => {
    switch (mode) {
      case "battery":
        return batteryCellIcon;
      case "signal":
        return signalCellIcon;
      case "backup":
        return backupCellIcon;
      case "pressure":
        return pressureCellIcon;
      case "tempDevice":
        return tempDeviceCellIcon;
      case "tempWater":
        return tempWaterCellIcon;
      default:
        return null;
    }
  };

  // if used as a single-column renderer on desktop
  if (mode !== "row") {
    const cell = pickSingleCellDesktop();
    return <div className="flex items-center justify-center">{cell}</div>;
  }

  // full row layout (battery | signal | backup | [pressure] | [temps])
  const cells: JSX.Element[] = [];

  cells.push(batteryCellIcon);
  cells.push(signalCellIcon);
  cells.push(backupCellIcon);

  if (hasPressure) {
    cells.push(
      <div key="pressure" className="flex justify-center">
        {pressureCellIcon}
      </div>
    );
  }

  if (showTemper) {
    cells.push(tempDeviceCellIcon ?? <span />);
    cells.push(
      <div key="tempWater" className="flex justify-center">
        {tempWaterCellIcon}
      </div>
    );
  }

  const totalCols = 3 + (hasPressure ? 1 : 0) + (showTemper ? 2 : 0) || 1;

  return (
    <div className="flex w-full justify-center">
      <div
        className="grid items-center gap-x-3"
        style={{
          gridTemplateColumns: `repeat(${totalCols}, minmax(0, 1fr))`,
        }}
      >
        {cells.map((cell, idx) => (
          <div key={idx} className="flex items-center justify-center">
            {cell}
          </div>
        ))}
      </div>
    </div>
  );
};
